import React, { useState, useEffect, useRef } from "react";
import { EMPLOYEES_TO_EVALUATE, AXES_CONFIG, INTERDEPENDENCIES } from "../../../../data/origami/employeeEvaluationData";

function RotaryDial({ axis, value, onChange, disabled, isAffected }) {
  const [isDragging, setIsDragging] = useState(false);
  const dialRef = useRef(null);

  // ✅ SIMPLE : 0% = haut (-90°), 100% = tour complet (270°)
  // const valueToAngle = (val) => -90 + (val / 100) * 360;
  const valueToAngle = (val) => (val / 100) * 360;

  const getAngleFromMouse = (clientX, clientY) => {
    const el = dialRef.current;
    if (!el) return 0;

    const rect = el.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const dx = clientX - centerX;
    const dy = clientY - centerY;

    // Angle en degrés (-180 à 180, 0 = droite)
    let angle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Convertir pour que 0 = haut
    angle = angle + 90;

    // Normaliser entre 0 et 360
    if (angle < 0) angle += 360;

    return angle;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled) return;

    const angle = getAngleFromMouse(e.clientX, e.clientY);

    // Convertir l'angle en valeur 0-100
    let newValue = Math.round((angle / 360) * 100);

    // Snap à des multiples de 5
    newValue = Math.round(newValue / 5) * 5;

    // Clamper entre 0 et 100
    newValue = Math.max(0, Math.min(100, newValue));

    onChange(newValue);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging]);

  const currentAngle = valueToAngle(value);

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Dial */}
      <div className="relative">
        {/* Background circle */}
        <svg width="120" height="120">
          {/* Track */}
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke="rgba(100, 116, 139, 0.3)"
            strokeWidth="8"
          />
          {/* Progress arc */}
          <circle
            cx="60"
            cy="60"
            r="50"
            fill="none"
            stroke={axis.color}
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={`${(value / 100) * (2 * Math.PI * 50)} ${2 * Math.PI * 50}`}
            style={{
              filter: isAffected ? "brightness(0.5)" : "drop-shadow(0 0 8px " + axis.color + ")",
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transition: 'stroke-dasharray 0.1s ease-out',
            }}
          />
        </svg>

        {/* Center button */}
        <div
          ref={dialRef}
          className={`
            absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2
            w-24 h-24 rounded-full
            flex items-center justify-center
            transition-all duration-200
            ${disabled || isAffected
              ? 'cursor-grab opacity-50'
              : 'cursor-grab active:cursor-grabbing hover:scale-105'
            }
          `}
          onMouseDown={handleMouseDown}
          style={{
            background: `radial-gradient(circle, ${axis.color}15, transparent 70%)`
          }}
        >
          {/* Indicator line */}
          <div
            className="absolute w-1 h-12 rounded-full pointer-events-none"
            style={{
              background: `linear-gradient(to bottom, ${axis.color}, transparent)`,
              transform: `rotate(${currentAngle}deg)`,
              transformOrigin: 'bottom center', // ✅ FIX : Origine en bas
              bottom: '50%', // ✅ FIX : Positionner à partir du centre
              left: '50%',
              marginLeft: '-0.5px',
              filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.8))',
              transition: 'transform 0.1s ease-out',
            }}
          />

          {/* Value display */}
          <div
            className="text-2xl font-bold select-none pointer-events-none z-10"
            style={{ color: axis.color }}
          >
            {value}
          </div>
        </div>
      </div>

      {/* Label */}
      <div className="text-center">
        <div className="text-sm font-semibold text-slate-200">
          {axis.label}
        </div>
        <div className="text-xs text-slate-500 max-w-[120px]">
          {axis.description}
        </div>
        {isAffected && (
          <div className="text-xs text-amber-400 mt-1">
            🔗 Auto-ajusté
          </div>
        )}
      </div>
    </div>
  );
}

function RadarChart({ values, axes }) {
  const size = 200;
  const center = size / 2;
  const radius = 70;
  const points = axes.map((axis, i) => {
    const angle = (i / axes.length) * 2 * Math.PI - Math.PI / 2;
    const value = values[axis.key] || 0;
    const r = (value / 100) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  });

  const polygonPoints = points.map(p => `${p.x},${p.y}`).join(" ");

  return (
    <svg width={size} height={size} className="mx-auto">
      {/* Grid circles */}
      {[20, 40, 60, 80, 100].map(percent => (
        <circle
          key={percent}
          cx={center}
          cy={center}
          r={(percent / 100) * radius}
          fill="none"
          stroke="rgba(100, 116, 139, 0.2)"
          strokeWidth="1"
        />
      ))}

      {/* Axes lines */}
      {axes.map((axis, i) => {
        const angle = (i / axes.length) * 2 * Math.PI - Math.PI / 2;
        const x2 = center + radius * Math.cos(angle);
        const y2 = center + radius * Math.sin(angle);
        return (
          <line
            key={i}
            x1={center}
            y1={center}
            x2={x2}
            y2={y2}
            stroke="rgba(100, 116, 139, 0.3)"
            strokeWidth="1"
          />
        );
      })}

      {/* Data polygon */}
      <polygon
        points={polygonPoints}
        fill="rgba(99, 102, 241, 0.2)"
        stroke="#6366f1"
        strokeWidth="2"
        style={{
          filter: "drop-shadow(0 0 8px rgba(99, 102, 241, 0.6))"
        }}
      />

      {/* Data points */}
      {points.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r="4"
          fill={axes[i].color}
          stroke="white"
          strokeWidth="2"
        />
      ))}
    </svg>
  );
}

export default function EmployeeEvaluator({ employee, onValidate, onClose }) {
  const [values, setValues] = useState({
    performance: 50,
    collaboration: 50,
    initiative: 50,
    adaptabilite: 50,
    alignement: 50,
    disponibilite: 50
  });

  const [affectedAxes, setAffectedAxes] = useState(new Set());
  const [showDossier, setShowDossier] = useState(true);

  const handleValueChange = (axisKey, newValue) => {
    const newValues = { ...values, [axisKey]: newValue };
    const newAffected = new Set();

    // Apply interdependencies
    Object.keys(INTERDEPENDENCIES).forEach(sourceKey => {
      if (sourceKey === axisKey) {
        const dep = INTERDEPENDENCIES[sourceKey];
        const affectedValue = dep.rule(newValue);
        newValues[dep.affects] = affectedValue;
        newAffected.add(dep.affects);
      }
    });

    setValues(newValues);
    setAffectedAxes(newAffected);
  };

  const checkSolution = () => {
    const solution = employee.solution;
    const tolerance = 10;

    return Object.keys(solution).every(key => {
      const diff = Math.abs(values[key] - solution[key]);
      return diff <= tolerance;
    });
  };

  const isCorrect = checkSolution();

  return (
    <div className="py-8 px-6 bg-gradient-to-br from-slate-950 via-indigo-950/20 to-slate-950 min-h-[80vh]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="text-2xl font-bold text-white mb-2">
              Évaluation annuelle — {employee.name}
            </div>
            <div className="text-sm text-slate-400">
              {employee.poste} • ID #{employee.id}
            </div>
          </div>

          <button
            onClick={() => setShowDossier(!showDossier)}
            className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm transition"
          >
            {showDossier ? "Masquer" : "Afficher"} dossier
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Dossier employé */}
          {showDossier && (
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
                <div className="text-lg font-semibold text-emerald-300 mb-3">
                  ✓ Points forts
                </div>
                <ul className="space-y-2 text-sm text-slate-300">
                  {employee.dossier.points_forts.map((point, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
                <div className="text-lg font-semibold text-orange-300 mb-3">
                  ⚠️ Points d'amélioration
                </div>
                <ul className="space-y-2 text-sm text-slate-300">
                  {employee.dossier.points_faibles.map((point, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-orange-400">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {employee.dossier.incidents.length > 0 && (
                <div className="bg-slate-900/60 border border-red-700/30 rounded-xl p-4">
                  <div className="text-lg font-semibold text-red-300 mb-3">
                    🚨 Incidents
                  </div>
                  <ul className="space-y-2 text-sm text-slate-300">
                    {employee.dossier.incidents.map((incident, i) => (
                      <li key={i} className="flex gap-2">
                        <span className="text-red-400">•</span>
                        <span>{incident}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-4">
                <div className="text-sm text-slate-400 mb-1">
                  Absences : <span className="text-white">{employee.dossier.absences}</span>
                </div>
              </div>

              {/* Grille de conversion */}
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                <div className="text-sm font-semibold text-indigo-300 mb-3">
                  📊 Grille d'évaluation
                </div>
                <div className="text-xs text-slate-400 space-y-2">
                  <div>
                    <span className="text-emerald-300">Excellent / Très :</span> 80-100
                  </div>
                  <div>
                    <span className="text-blue-300">Bon / Solide :</span> 60-80
                  </div>
                  <div>
                    <span className="text-amber-300">Correct / Modeste :</span> 40-60
                  </div>
                  <div>
                    <span className="text-orange-300">Difficile / Faible :</span> 20-40
                  </div>
                  <div>
                    <span className="text-red-300">Très difficile / Absent :</span> 0-20
                  </div>
                  <div className="pt-2 border-t border-slate-700 mt-2">
                    <span className="text-slate-300">Absences :</span>
                    <br />0-1j → 90-100 | 2-3j → 60-90 | 4+j → &lt;60
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Evaluation section */}
          <div className={showDossier ? "lg:col-span-2" : "lg:col-span-3"}>
            {/* Radar chart */}
            <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-6 mb-6">
              <div className="text-lg font-semibold text-white mb-4 text-center">
                Profil d'évaluation
              </div>
              <RadarChart values={values} axes={AXES_CONFIG} />
            </div>

            {/* Dials */}
            <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-6 mb-6">
              <div className="text-lg font-semibold text-white mb-6">
                Ajuster les critères
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {AXES_CONFIG.map(axis => (
                  <RotaryDial
                    key={axis.key}
                    axis={axis}
                    value={values[axis.key]}
                    onChange={(val) => handleValueChange(axis.key, val)}
                    disabled={false}
                    isAffected={affectedAxes.has(axis.key)}
                  />
                ))}
              </div>

              {/* Interdependencies info */}
              <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="text-sm text-amber-300 font-semibold mb-2">
                  ⚙️ Interdépendances automatiques
                </div>
                <div className="text-xs text-slate-400 space-y-1">
                  {Object.entries(INTERDEPENDENCIES).map(([key, dep]) => (
                    <div key={key}>
                      • {AXES_CONFIG.find(a => a.key === key)?.label} → {AXES_CONFIG.find(a => a.key === dep.affects)?.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Validation */}
            <div className="flex items-center justify-between">
              <button
                onClick={onClose}
                className="px-6 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold transition"
              >
                Retour
              </button>

              <button
                onClick={() => isCorrect && onValidate()}
                disabled={!isCorrect}
                className={`
                  px-6 py-3 rounded-lg font-semibold transition
                  ${isCorrect
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                  }
                `}
              >
                {isCorrect ? '✓ Valider l\'évaluation' : 'Ajustez les critères'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}